import { useState, useEffect, useCallback } from 'react'
import { useAuth } from './hooks/useAuth'
import LoadingScreen from './components/common/LoadingScreen'
import LoginScreen from './components/auth/LoginScreen'
import SignupFlow from './components/auth/SignupFlow'
import ChangePasswordScreen from './components/auth/ChangePasswordScreen'
import AppShell from './components/layout/AppShell'
import AdminShell from './admin/AdminShell'
import BillingPage from './pages/BillingPage'
import OnboardingWizard from './pages/OnboardingWizard'
import NavBar from './components/layout/NavBar'
import { decideAccess } from '@petrotrack/shared'

// ─── Minimal History-API router ───────────────────────────────────────────────

function useRouter() {
  const [path, setPath] = useState(() => window.location.pathname)

  useEffect(() => {
    function handler() { setPath(window.location.pathname) }
    window.addEventListener('popstate', handler)
    return () => window.removeEventListener('popstate', handler)
  }, [])

  const navigate = useCallback((to) => {
    if (window.location.pathname !== to) {
      window.history.pushState(null, '', to)
      setPath(to)
    }
  }, [])

  return { path, navigate }
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  const { path, navigate } = useRouter()

  // "Pay immediately" deep-link from the landing page: /signup?plan=growth&pay=1
  // Captured once on mount so it survives the URL change after we redirect.
  const [signupIntent, setSignupIntent] = useState(() => {
    const p = new URLSearchParams(window.location.search)
    const plan = p.get('plan') || ''
    const pay = p.get('pay') === '1'
    return plan || pay ? { plan, pay } : null
  })

  const {
    currentUser,
    authUser,
    loading,
    loginError,
    signUpError,
    isPasswordRecovery,
    login,
    signUp,
    logout,
    deleteAccount,
    changePassword,
    sendPasswordReset,
    resendVerification,
    refetchProfile,
  } = useAuth()

  // ── Resolve which screen to render ──────────────────────────────────────────
  //
  //  Priority order:
  //  1. Still bootstrapping → LoadingScreen
  //  2. Password-recovery flow → ChangePasswordScreen
  //  3. Not authenticated:
  //       /signup → SignupFlow
  //       anything else → LoginScreen
  //  4. Authenticated → main AppShell
  //
  //  Self-service signups are provisioned server-side (email auto-confirmed,
  //  organisation + first branch + admin profile created) and land straight
  //  on the dashboard. Admin-created users go through the change-password gate.

  if (loading) return <LoadingScreen />

  // Password recovery (detected from URL hash/query by useAuth)
  if (isPasswordRecovery) {
    return (
      <ChangePasswordScreen
        currentUser={null}
        onChangePassword={changePassword}
        onLogout={logout}
        title="Set New Password"
      />
    )
  }

  // ── Unauthenticated ──────────────────────────────────────────────────────────
  if (!currentUser && !authUser) {
    if (path === '/signup') {
      return (
        <SignupFlow
          onSignUp={signUp}
          signUpError={signUpError}
          onGoToLogin={() => navigate('/login')}
          initialPlan={signupIntent?.plan}
          onResendVerification={resendVerification}
        />
      )
    }
    return (
      <LoginScreen
        onLogin={login}
        error={loginError}
        onPasswordReset={sendPasswordReset}
        onGoToSignup={() => navigate('/signup')}
      />
    )
  }

  // ── Authenticated — must change password (admin-created accounts) ────────────
  if (currentUser?.mustChangePassword) {
    return (
      <ChangePasswordScreen
        currentUser={currentUser}
        onChangePassword={changePassword}
        onLogout={logout}
      />
    )
  }

  // ── Authenticated — redirect public routes to dashboard ─────────────────────
  // Paid signups (pay=1) land on the billing page so the Paystack popup can
  // fire immediately; free-trial signups go straight to the dashboard.
  const publicPaths = ['/login', '/signup', '/verify-email', '/onboarding']
  if (publicPaths.includes(path)) {
    navigate(signupIntent?.pay ? '/billing' : currentUser?.role === 'superadmin' ? '/admin' : '/dashboard')
  }

  // Auth user exists but profile fetch hasn't resolved yet — keep showing loader.
  if (!currentUser) return <LoadingScreen />

  // ── Trial/subscription gate ──────────────────────────────────────────────────
  // The rule lives in @petrotrack/shared so this and the mobile app can't
  // drift: superadmin and comped are never locked, a manual suspension always
  // locks, and a lapsed payer keeps access until their grace window ends.
  const access = decideAccess({
    role: currentUser.role,
    subscriptionStatus: currentUser.subscriptionStatus,
    trialEndsAt: currentUser.trialEndsAt,
    suspendedAt: currentUser.suspendedAt,
    graceUntil: currentUser.graceUntil,
    onboardingCompleted: currentUser.onboardingCompleted,
  })
  const isLocked = access.locked

  // Billing page is accessible when locked (to subscribe) or explicitly navigated.
  if (isLocked || path === '/billing') {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar
          currentUser={currentUser}
          onLogout={logout}
          onProfileClick={() => navigate('/billing')}
        />
        <main className="max-w-2xl mx-auto px-4 py-6">
          {isLocked && path !== '/billing' && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-4">
              <p className="text-sm font-semibold text-red-700">
                {access.reason === 'suspended' ? 'Account suspended'
                  : access.reason === 'subscription_lapsed' ? 'Subscription not active'
                  : 'Your trial has ended'}
              </p>
              <p className="text-xs text-red-600 mt-1">{access.message}</p>
              {access.reason === 'suspended' && currentUser.suspendedReason && (
                <p className="text-xs text-red-600 mt-1">Reason: {currentUser.suspendedReason}</p>
              )}
            </div>
          )}
          <BillingPage
            currentUser={currentUser}
            autoSubscribeTier={signupIntent?.pay ? signupIntent.plan : null}
            onAutoSubscribeStarted={() => setSignupIntent(null)}
            onContinue={() => navigate('/dashboard')}
          />
        </main>
      </div>
    )
  }

  // ── First-run setup wizard ───────────────────────────────────────────────────
  // Brand-new admins (onboarding_completed=false) are guided through naming their
  // branch, adding tanks/pumps and inviting staff before they reach the dashboard.
  if (currentUser.role === 'admin' && currentUser.onboardingCompleted === false) {
    return <OnboardingWizard currentUser={currentUser} onComplete={refetchProfile} />
  }

  // ── Superadmin console (HQ) ─────────────────────────────────────────────────
  // Superadmins land in HQ; /dashboard still renders the customer app (reached
  // via HQ's "Open customer app"), with a floating pill to jump back.
  if (currentUser.role === 'superadmin' && path !== '/dashboard') {
    return <AdminShell currentUser={currentUser} onLogout={logout} navigate={navigate} />
  }

  return (
    <>
      <AppShell currentUser={currentUser} onLogout={logout} onDeleteAccount={deleteAccount} navigate={navigate} />
      {currentUser.role === 'superadmin' && (
        <button
          onClick={() => navigate('/admin')}
          className="fixed bottom-24 right-4 z-[60] flex items-center gap-1.5 bg-[#0E1817] text-teal-300 text-xs font-bold px-3.5 py-2.5 rounded-full shadow-lg border border-teal-400/30 hover:bg-[#15221F] transition"
        >
          ← Back to HQ
        </button>
      )}
    </>
  )
}
